import { Service, inject, signal } from '@angular/core';
import {firstValueFrom, forkJoin, Observable, Subject} from 'rxjs';
import {TagUtils} from '../utils/tag-utils';
import {Specification} from '../entity/specification';
import {SpecificationCharacteristic} from '../entity/specification-characteristic';
import {ModelService} from '../services/model-service';
import {EntityLineHeader} from '../entity/entity-line-header';
import {EntityLineService} from '../services/entity-line-service';
import {ConfigurationSaveInformation} from '../model/configuration-save-information';
import {RatingService} from '../services/rating.service';
import {GuruEntity} from '../model/guru-entity';
import {UnitRatingResponse} from '../model/unit-rating-response';
import {LineResequenceInput} from '../model/line-resequence-input';
import {EntityLine} from '../entity/entity-line';
import {SfrManagementService} from '../services/sfr-management-service';

@Service()
export class ConfigurationService {
  private readonly modelService = inject(ModelService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly sfrManagementService = inject(SfrManagementService);
  private readonly ratingService = inject(RatingService);


  private lineChangedSubject: Subject<string> = new Subject<string>();
  readonly isBusy = signal<boolean>(undefined!);

  loadEngine(modelPropertyName: string, leadTimeCode: string): Observable<any> {
    const subject = new Subject<any>();
    this.modelService.getEngineKey(modelPropertyName).subscribe({
      next: (engineKey) => {
        this.modelService.getEntitiesStartingWithName(engineKey, 'USLeadTime').subscribe({
          next: (entities) => {
            if ((entities?.length || 0) > 0) {
              this.modelService.setValue('USLeadTime', engineKey, leadTimeCode).subscribe({
                next: () => {
                  this.initializeEngine(subject, engineKey);
                }, error: (error) => this.handleError(error, subject)
              });
            } else {
              this.initializeEngine(subject, engineKey);
            }
          }, error: (error) => this.handleError(error, subject)
        });
      }, error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  private initializeEngine(subject: Subject<any>, engineKey: string): void {
    this.modelService.executeHierarchy(engineKey, 'hierDefaultOnce').subscribe({
      next: (updatedEntities) => {
        this.modelService.executeHierarchy(engineKey, 'hierSKU').subscribe({
          next: () => {
            this.modelService.executeHierarchy(engineKey, 'hierPRN').subscribe({
              next: () => {
                this.modelService.executeHierarchy(engineKey, 'hierDOC').subscribe({
                  next: () => {
                    this.modelService.executeHierarchy(engineKey, 'hierUserSelections').subscribe({
                      next: (entities) => subject.next({engineKey: engineKey, selectionEntities: entities}),
                      error: (error) => this.handleError(error, subject)
                    });
                  }, error: (error) => this.handleError(error, subject)
                });
              }, error: (error) => this.handleError(error, subject)
            });
          }, error: (error) => this.handleError(error, subject)
        });
      }, error: (error) => this.handleError(error, subject)
    });
  }

  loadConfiguration(modelPropertyName: string, entityLineHeader: EntityLineHeader, leadTimeCode: string): Observable<any> {
    const subject = new Subject<any>();
    this.loadEngine(modelPropertyName, leadTimeCode).subscribe({
      next: (engine) => {
        this.entityLineService.getFullEntityLine(entityLineHeader).subscribe({
          next: (line) => {
            if (line && line.singleEntityLines && line.singleEntityLines.length > 0) {
              const specifications: Specification[] = line.singleEntityLines[0].specifications;
              const selectionNames: string[] = engine.selectionEntities.map((selection: any) => selection.Name);
              const characteristics: SpecificationCharacteristic[] =
                specifications.flatMap(spec => spec.specificationCharacteristics)
                  .filter(spec => spec.key !== 'USLeadTime' && selectionNames.indexOf(spec.key) >= 0);
              this.modelService.loadConfiguration(engine.engineKey, characteristics).subscribe({
                next: () => {
                  const entityId: string = entityLineHeader.key.entityId;
                  const revision: number = entityLineHeader.key.entityRevision;
                  const lineId: string = entityLineHeader.lineId;
                  this.sfrManagementService.getSfrEntries(entityId, revision, lineId).subscribe({
                    next: (sfrEntries) => {
                      subject.next({
                        engineKey: engine.engineKey,
                        line: line,
                        taggingDetails: TagUtils.createTaggingDetails(line.singleEntityLines),
                        sfrEntries: sfrEntries
                      });
                    }, error: (error) => this.handleError(error, subject)
                  });
                }, error: (error) => this.handleError(error, subject)
              });
            } else {
              subject.error('Could not load line');
            }
          }, error: (error) => this.handleError(error, subject)
        });
      }, error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  private handleError(error: any, subject: Subject<any>): void {
    this.isBusy.set(false);
    subject.error(error);
  }

  validateUnit(engineKey: string): Observable<any> {
    const subject = new Subject<any>();
    this.modelService.executeHierarchy(engineKey, 'hierSKU').subscribe({
      next: () => {
        this.modelService.executeHierarchy(engineKey, 'hierPRN').subscribe({
          next: () => {
            this.modelService.getInvalidEntities(engineKey).subscribe({
              next: (invalidEntities) => {
                this.modelService.getEntitiesStartingWithName(engineKey, 'US').subscribe({
                  next: (selections) => {
                    subject.next({
                      invalidEntities: invalidEntities,
                      userSelections: selections
                    });
                  }, error: (error) => this.handleError(error, subject)
                });
              }, error: (error) => this.handleError(error, subject)
            });
          }, error: (error) => this.handleError(error, subject)
        });
      }, error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  changeUnitType(engineKey: string): Observable<any> {
    return this.changeSelectionWithDefaultsHierarchy(engineKey, 'hierModelDefaults');
  }

  private changeSelectionWithDefaultsHierarchy(engineKey: string, hierarchyName: string): Observable<any> {
    const subject = new Subject<any>();
    this.modelService.executeHierarchy(engineKey, hierarchyName).subscribe({
      next: () => {
        this.validateUnit(engineKey).subscribe({
          next: (validationResult) => {
            this.modelService.getCharacteristicValue(engineKey, 'USUnit', 'FileName').subscribe({
              next: (imageFileName) => {
                subject.next({
                  invalidEntities: validationResult.invalidEntities,
                  selectionEntities: validationResult.userSelections,
                  imageFileName: imageFileName
                });
              }, error: (error) => this.handleError(error, subject)
            });
          }, error: (error) => this.handleError(error, subject)
        });
      }, error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  changeSystemType(engineKey: string): Observable<any> {
    return this.changeSelectionWithDefaultsHierarchy(engineKey, 'hierSystemTypeDefaults');
  }

  changeLine(lineNumber: string): void {
    this.lineChangedSubject.next(lineNumber);
  }

  lineChanged(): Observable<string> {
    return this.lineChangedSubject.asObservable();
  }

  getMessages(engineKey: string): Observable<string[]> {
    return this.modelService.getMessages(engineKey);
  }

  saveConfiguration(saveInformation: ConfigurationSaveInformation): Observable<string> {
    const subject = new Subject<string>();
    this.modelService.saveConfiguration(saveInformation).subscribe({
      next: () => subject.next('Configuration saved'),
      error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  saveConfigurations(configurations: ConfigurationSaveInformation[]): Observable<EntityLine[]> {
    const subject = new Subject<EntityLine[]>();
    this.modelService.saveConfigurations(configurations).subscribe({
      next: (lines) => subject.next(lines),
      error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  getRatingResponse(engineKey: string, selectionEntities: GuruEntity[]): Observable<UnitRatingResponse> {
    const subject = new Subject<UnitRatingResponse>();
    this.ratingService.getRatingResponse(engineKey, selectionEntities).subscribe({
      next: (result) => subject.next(result),
      error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  setEngineRatingValues(engineKey: string, product: string, rating: UnitRatingResponse): Observable<string> {
    return this.modelService.setEngineRatingValues(engineKey, product, rating);
  }

  addProduct(productId: string, information: ConfigurationSaveInformation): Observable<EntityLineHeader> {
    const subject = new Subject<EntityLineHeader>();
    this.modelService.addConfiguration(productId, information).subscribe({
      next: (response) => subject.next(response),
      error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  copyConfiguration(information: ConfigurationSaveInformation): Observable<EntityLineHeader> {
    const subject = new Subject<EntityLineHeader>();
    this.modelService.copyConfiguration(information).subscribe({
      next: (response) => subject.next(response),
      error: (error) => this.handleError(error, subject)
    });
    return subject.asObservable();
  }

  async applyMassUpdate(engineKey: string, entities: GuruEntity[]): Promise<void> {
    for (const entity of entities) {
      await firstValueFrom<string>(this.modelService.setValue(entity.Name, engineKey, entity.Value.toString()));
    }
  }

  resequenceLines(input: LineResequenceInput): Observable<string> {
    return this.modelService.resequenceLines(input);
  }

}
